/**
 * ATLETAHUB — HEO-B: TRATAMENTO E NORMALIZAÇÃO DE ERROS
 * 
 * Centralização, classificação canônica, sanitização de segurança e normalização
 * de respostas de erro da API.
 */

export type ErrorCode =
  | 'VALIDATION_ERROR'
  | 'AUTHENTICATION_ERROR'
  | 'AUTHORIZATION_ERROR'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'DEPENDENCY_ERROR'
  | 'TIMEOUT'
  | 'INTERNAL_ERROR';

/**
 * Base Application Error
 */
export class AppError extends Error {
  public readonly statusCode: number;
  public readonly code: ErrorCode;
  public readonly details?: any;
  public readonly isOperational: boolean;

  constructor(message: string, statusCode = 500, code: ErrorCode = 'INTERNAL_ERROR', details?: any) {
    super(message);
    this.name = this.constructor.name;
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;
    this.isOperational = true;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

export class ValidationError extends AppError {
  constructor(message = 'Dados inválidos ou campos obrigatórios ausentes.', details?: any) {
    super(message, 400, 'VALIDATION_ERROR', details);
  }
}

export class AuthenticationError extends AppError {
  constructor(message = 'Não autorizado. Token de autenticação inválido ou ausente.', details?: any) {
    super(message, 401, 'AUTHENTICATION_ERROR', details);
  }
}

export class AuthorizationError extends AppError {
  constructor(message = 'Acesso negado. Permissões insuficientes.', details?: any) {
    super(message, 403, 'AUTHORIZATION_ERROR', details);
  }
}

export class NotFoundError extends AppError {
  constructor(message = 'Recurso não encontrado.', details?: any) {
    super(message, 404, 'NOT_FOUND', details);
  }
}

export class ConflictError extends AppError {
  constructor(message = 'Conflito na operação ou recurso já existente/bloqueado.', details?: any) {
    super(message, 409, 'CONFLICT', details);
  }
}

export class RateLimitError extends AppError {
  constructor(message = 'Muitas requisições. Tente novamente em alguns instantes.', details?: any) {
    super(message, 429, 'RATE_LIMITED', details);
  }
}

export class DependencyError extends AppError {
  constructor(message = 'Falha de comunicação com dependência de serviço externo.', details?: any) {
    super(message, 503, 'DEPENDENCY_ERROR', details);
  }
}

export class TimeoutError extends AppError {
  constructor(message = 'Tempo limite de operação excedido.', details?: any) {
    super(message, 504, 'TIMEOUT', details);
  }
}

export class InternalError extends AppError {
  constructor(message = 'Ocorreu um erro interno no servidor.', details?: any) {
    super(message, 500, 'INTERNAL_ERROR', details);
  }
}

/**
 * Sanitiza mensagens de erro removendo tokens, chaves, senhas e traces de pilha.
 */
export function sanitizeErrorMessage(message?: string): string {
  if (!message || typeof message !== 'string') {
    return 'Ocorreu um erro interno no servidor.';
  }

  let sanitized = message;

  // Redigir chaves Asaas expressamente primeiro
  sanitized = sanitized.replace(/(\$aact_[A-Za-z0-9_=-]+)/gi, '[REDACTED_API_KEY]');

  // Redigir tokens genéricos, senhas e chaves
  sanitized = sanitized.replace(/(api_key|apiKey|token|secret|password|authorization)=([^\s&]+)/gi, (match, p1, p2) => {
    if (p2.includes('[REDACTED_API_KEY]')) {
      return `${p1}=[REDACTED_API_KEY]`;
    }
    return `${p1}=[REDACTED]`;
  });
  sanitized = sanitized.replace(/(Bearer\s+)[A-Za-z0-9_.-]+/gi, '$1[REDACTED_TOKEN]');

  // Redigir credenciais de connection strings / Firestore secrets
  sanitized = sanitized.replace(/(private_key|privateKey)=([^\s&]+)/gi, '$1=[REDACTED]');

  // Redigir credenciais embutidas em connection strings e URLs com userinfo (ex: postgresql://user:pass@host:port/db)
  sanitized = sanitized.replace(/([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)([^:\s\/@]*):([^@\s\/]+)@/g, '$1$2:[REDACTED]@');

  // Truncar stack trace se houver quebra de linha com "at "
  if (sanitized.includes('\n') || sanitized.includes('    at ')) {
    sanitized = sanitized.split('\n')[0].replace(/^Error:\s*/, '').trim();
  }

  // Remover referências a caminhos de arquivo internos (ex: /internal/db/client.ts:44 ou at client.ts)
  sanitized = sanitized.replace(/\s*at\s+([\/\\a-zA-Z0-9_.-]+:\d+(:\d+)?|\([^\)]+\))/g, '');
  sanitized = sanitized.replace(/\s+at\s+\/[a-zA-Z0-9_.\/-]+/g, '');
  sanitized = sanitized.replace(/(\/[a-zA-Z0-9_.-]+){2,}\.[a-zA-Z0-9]+(:\d+)?/g, '[INTERNAL_PATH]');

  return sanitized.trim();
}

const SENSITIVE_KEY_PATTERNS = [
  /api[-_]?key/i,
  /token/i,
  /secret/i,
  /password/i,
  /authorization/i,
  /private[-_]?key/i,
  /connection[-_]?string/i,
  /credential/i,
  /stack/i,
  /credit[-_]?card/i,
  /card[-_]?number/i,
  /cvv/i,
  /hash/i,
  /salt/i
];

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key));
}

/**
 * Sanitiza recursivamente objetos de detalhes garantindo que campos sensíveis
 * como API keys, senhas, tokens e stack traces nunca sejam expostos ao cliente.
 */
export function sanitizeErrorDetails(details: any): any {
  if (details === null || details === undefined) {
    return undefined;
  }

  if (typeof details === 'string') {
    return sanitizeErrorMessage(details);
  }

  if (typeof details === 'number' || typeof details === 'boolean') {
    return details;
  }

  if (Array.isArray(details)) {
    return details
      .map((item) => sanitizeErrorDetails(item))
      .filter((item) => item !== undefined);
  }

  if (typeof details === 'object') {
    const sanitizedObj: Record<string, any> = {};
    for (const [key, value] of Object.entries(details)) {
      if (isSensitiveKey(key)) {
        sanitizedObj[key] = '[REDACTED]';
        continue;
      }
      const sanitizedValue = sanitizeErrorDetails(value);
      if (sanitizedValue !== undefined) {
        sanitizedObj[key] = sanitizedValue;
      }
    }
    return Object.keys(sanitizedObj).length > 0 ? sanitizedObj : undefined;
  }

  return undefined;
}

/**
 * Extrai ou gera Correlation ID / Request ID único
 */
export function extractOrGenerateRequestId(req?: any): string {
  const headerId = req?.headers?.['x-request-id'] || req?.headers?.['x-correlation-id'];
  if (typeof headerId === 'string' && headerId.trim()) {
    return headerId.trim();
  }
  if (req?.requestId && typeof req.requestId === 'string') {
    return req.requestId;
  }
  return `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export interface NormalizedErrorResponse {
  success: false;
  error: string;
  code: ErrorCode;
  requestId: string;
  statusCode: number;
  details?: any;
}

/**
 * Mapeia status HTTP para ErrorCode canônico
 */
export function mapStatusToErrorCode(status: number): ErrorCode {
  switch (status) {
    case 400: return 'VALIDATION_ERROR';
    case 401: return 'AUTHENTICATION_ERROR';
    case 403: return 'AUTHORIZATION_ERROR';
    case 404: return 'NOT_FOUND';
    case 409: return 'CONFLICT';
    case 429: return 'RATE_LIMITED';
    case 502:
    case 503: return 'DEPENDENCY_ERROR';
    case 504: return 'TIMEOUT';
    default: return 'INTERNAL_ERROR';
  }
}

/**
 * Constrói payload de resposta normalizado preservando compatibilidade com clientes existentes
 * e garantindo que erros internos genéricos nunca vazem detalhes internos.
 */
export function formatErrorResponse(err: any, req?: any): { status: number; body: NormalizedErrorResponse } {
  const requestId = extractOrGenerateRequestId(req);

  let statusCode = 500;
  let code: ErrorCode = 'INTERNAL_ERROR';
  let publicMessage = 'Ocorreu um erro interno no servidor.';
  let rawDetails: any = undefined;

  if (err instanceof AppError) {
    statusCode = err.statusCode;
    code = err.code;
    rawDetails = err.details;
    if (err.isOperational && code !== 'INTERNAL_ERROR') {
      publicMessage = sanitizeErrorMessage(err.message);
    } else {
      publicMessage = 'Ocorreu um erro interno no servidor.';
    }
  } else if (err?.statusCode && typeof err.statusCode === 'number' && err.statusCode !== 500) {
    statusCode = err.statusCode;
    code = mapStatusToErrorCode(statusCode);
    publicMessage = sanitizeErrorMessage(err.message || 'Erro na requisição');
    rawDetails = err.details;
  } else if (err?.status && typeof err.status === 'number' && err.status !== 500) {
    statusCode = err.status;
    code = mapStatusToErrorCode(statusCode);
    publicMessage = sanitizeErrorMessage(err.message || 'Erro na requisição');
  } else if (err?.code === 'auth/id-token-expired' || err?.code === 'auth/argument-error') {
    statusCode = 401;
    code = 'AUTHENTICATION_ERROR';
    publicMessage = 'Token de autenticação inválido ou expirado.';
  } else if (typeof err?.message === 'string') {
    const msg = err.message.toLowerCase();
    if (msg.includes('não autorizado') || msg.includes('unauthorized') || (msg.includes('token') && (msg.includes('inválido') || msg.includes('expirado') || msg.includes('ausente')))) {
      statusCode = 401;
      code = 'AUTHENTICATION_ERROR';
      publicMessage = 'Não autorizado. Token de autenticação inválido ou ausente.';
    } else if (msg.includes('acesso negado') || msg.includes('forbidden') || msg.includes('permissão') || msg.includes('permission-denied')) {
      statusCode = 403;
      code = 'AUTHORIZATION_ERROR';
      publicMessage = 'Acesso negado. Permissões insuficientes.';
    } else if (msg.includes('não encontrad') || msg.includes('not found') || msg.includes('not_found')) {
      statusCode = 404;
      code = 'NOT_FOUND';
      publicMessage = sanitizeErrorMessage(err.message);
    } else if (msg.includes('conflito') || msg.includes('já existe') || msg.includes('já está ocupad') || msg.includes('bloquead')) {
      statusCode = 409;
      code = 'CONFLICT';
      publicMessage = sanitizeErrorMessage(err.message);
    } else if (msg.includes('timed out') || msg.includes('timeout')) {
      statusCode = 504;
      code = 'TIMEOUT';
      publicMessage = 'Tempo limite de operação excedido.';
    } else {
      // Erro inesperado / genérico: SEMPRE mensagem genérica segura para o cliente
      statusCode = 500;
      code = 'INTERNAL_ERROR';
      publicMessage = 'Ocorreu um erro interno no servidor.';
    }
  } else {
    statusCode = 500;
    code = 'INTERNAL_ERROR';
    publicMessage = 'Ocorreu um erro interno no servidor.';
  }

  const sanitizedDetails = sanitizeErrorDetails(rawDetails);

  return {
    status: statusCode,
    body: {
      success: false,
      error: publicMessage,
      code,
      requestId,
      statusCode,
      ...(sanitizedDetails !== undefined ? { details: sanitizedDetails } : {})
    }
  };
}

/**
 * Log seguro de erro sem vazar credenciais ou dados sensíveis
 */
export function logSafeError(err: any, req?: any): void {
  const requestId = extractOrGenerateRequestId(req);
  const method = req?.method || 'INTERNAL';
  const path = req?.originalUrl || req?.url || req?.path || '/';
  const errorMsg = sanitizeErrorMessage(err?.message || String(err));
  const code = err instanceof AppError ? err.code : mapStatusToErrorCode(err?.statusCode || 500);

  console.error(`[ERROR_LOG] [${requestId}] [${method} ${path}] [${code}]: ${errorMsg}`);
}

/**
 * Express Middleware para injetar requestId
 */
export function requestIdMiddleware(req: any, res: any, next: any) {
  const reqId = extractOrGenerateRequestId(req);
  req.requestId = reqId;
  if (res && typeof res.setHeader === 'function') {
    res.setHeader('x-request-id', reqId);
  }
  next();
}

/**
 * Express Middleware centralizado para tratamento de erros
 */
export function centralizedErrorHandler(err: any, req: any, res: any, next: any) {
  logSafeError(err, req);
  const { status, body } = formatErrorResponse(err, req);
  if (res && typeof res.setHeader === 'function') {
    res.setHeader('x-request-id', body.requestId);
  }
  return res.status(status).json(body);
}

export { httpObservabilityMiddleware, normalizeHttpPath } from '../logging/HttpObservabilityMiddleware';
export {
  HttpMetricsAggregator,
  getHttpStatusClass,
  buildMetricGroupKey,
  globalHttpMetricsAggregator,
} from '../logging/HttpMetricsAggregator';


